import { File as FileModel } from "@/models/file/file.model"
import { FileBucket } from "@/models/file-bucket.model"
import { FileUploadContexts } from "@/enums/file-upload-contexts"
import { FileRelation } from "@/models/file/file-relation.model"
import { ModelFactory } from "@/factories/model-factory"
import { FileBucketRepository } from "@/repositories/file-bucket-repository"
import ModelManager from "@/services/model-manager"
import { FileRelationRepository } from "@/repositories/file-relation.repository"
import { useMessageStore } from "@/store/messages.store"
import { useServiceProviderDateStore } from "@/store/service-provider-date.store"
import { useInstructionStore } from "@/store/instructions.store"
import { useSaleStore } from "@/store/sale.store"
import { usePurchaseStore } from "@/store/purchase.store"
import { computed } from "vue"
import { storeToRefs } from "pinia"
import { FileRepository } from "@/repositories/file-repository"
import { useRentalIntakeStore } from "@/store/rental-intake.store"
import { useRentalDispenseStore } from "@/store/rental-dispense.store"
import { useSocketSynchronizationHandlers } from "@/composables/socket/socket-synchronization-handlers"
import { BaseController } from "@/controllers/model-controllers/abstract/base-controller"
import { useRelatedObjectsManager } from "@/composables/socket/related-objects-manager"
import { useInstructionFeedbacksActivitiesStore } from "@/store/instruction-feedbacks-activities.store"
import { useIncomingInvoiceStore } from "@/store/incoming-invoices.store.ts"
import { useWorkspaceStore } from "@/workspace/store/workspace.store"
import type { BaseModel } from "@/models/base.model.ts"

export class FileController extends BaseController {
  private messageStore = useMessageStore()
  private serviceProviderDateStore = useServiceProviderDateStore()
  private instructionStore = useInstructionStore()
  private saleStore = useSaleStore()
  private purchaseStore = usePurchaseStore()
  private rentalIntakeStore = useRentalIntakeStore()
  private rentalDispenseStore = useRentalDispenseStore()
  private instructionFeedbackActivitiesStore = useInstructionFeedbacksActivitiesStore()
  private incomingInvoiceStore = useIncomingInvoiceStore()
  private workspaceStore = useWorkspaceStore()

  private allFileBuckets = computed(() => {
    const { messages } = storeToRefs(this.messageStore)
    const { serviceProviderDatesList } = storeToRefs(this.serviceProviderDateStore)
    const { instructions, instructionFeedbacks } = storeToRefs(this.instructionStore)
    const { sales } = storeToRefs(this.saleStore)
    const { purchases } = storeToRefs(this.purchaseStore)
    const { rentalIntakes } = storeToRefs(this.rentalIntakeStore)
    const { rentalDispenses } = storeToRefs(this.rentalDispenseStore)
    const { planningInstructionFeedbackActivities } = storeToRefs(
      this.instructionFeedbackActivitiesStore
    )
    const { incomingInvoices } = storeToRefs(this.incomingInvoiceStore)
    const { notes } = storeToRefs(this.workspaceStore)

    return collectAllFileBuckets([
      messages.value,
      serviceProviderDatesList.value,
      instructions.value,
      instructionFeedbacks.value,
      planningInstructionFeedbackActivities.value,
      sales.value,
      purchases.value,
      rentalIntakes.value,
      rentalDispenses.value,
      incomingInvoices.value,
      notes.value
    ])
  })

  constructor() {
    super()

    const { pushToRelatedObjects, removeFromRelatedObjects } = useRelatedObjectsManager(
      this.allFileBuckets,
      (fileRelation: FileRelation) => fileRelation.fileBucket,
      (fileBucket: FileBucket) => fileBucket.id!,
      (fileBucket: FileBucket) => fileBucket.fileRelations
    )
    this.socketSynchronizationHandlers = useSocketSynchronizationHandlers(
      FileRelation,
      null,
      pushToRelatedObjects,
      removeFromRelatedObjects,
      false // False because files from service provider app will otherwise not be added for the same session
    )

    const allFileRelations = computed(() => {
      return this.allFileBuckets.value.flatMap((bucket) => bucket.fileRelations)
    })

    const {
      pushToRelatedObjects: pushFileToRelatedObjects,
      removeFromRelatedObjects: removeFileFromRelatedObjects
    } = useRelatedObjectsManager(
      allFileRelations,
      (file: FileModel) => file.fileRelation,
      (fileRelation: FileRelation) => fileRelation.file?.id,
      (fileRelation: FileRelation) => [fileRelation.file]
    )

    useSocketSynchronizationHandlers(
      FileModel,
      null,
      pushFileToRelatedObjects,
      removeFileFromRelatedObjects
    )
  }

  /**
   * Link a file to a file bucket.
   * @param file The file to link.
   * @param fileBucket The file bucket to link the file to.
   * @param fileUploadContext The context of the file upload.
   */
  public async linkFile(
    file: FileModel,
    fileBucket: FileBucket,
    fileUploadContext: FileUploadContexts
  ): Promise<FileRelation> {
    const fileRelation = ModelFactory.createModel(FileRelation)
    fileRelation.file = file
    fileRelation.context = fileUploadContext

    // Important to push the file before response. Otherwise an issue will occur where uploading (a) file(s) to a
    // (standalone) GeneralProcessingRow, and subsequently choosing a concrete transaction type, will cause the file(s)
    // to not be copied to the concrete row. // TODO check if this is still relevant after file refactor
    fileBucket.fileRelations.push(fileRelation)

    // Link the file to the related model, receive a proper FileRelation from the server.
    const response = await new FileBucketRepository().linkFileRelationToBucket(
      fileRelation,
      fileBucket
    )
    fileRelation.id = response.data.id // ModelManager needs to know the id of the new fileRelation to update it

    // TODO: line below will cause data leakage from service provider column to other columns
    ModelManager.getInstance().update(FileRelation, response.data) // Update the fileRelation with the proper object

    return fileRelation
  }

  /**
   * Delete a file relation.
   * @param fileRelation The file relation to delete.
   * @param onDeleted
   */
  public async deleteFileRelation(
    fileRelation: FileRelation,
    onDeleted?: (val: FileRelation) => void
  ) {
    if (fileRelation.isSaved) {
      await new FileRelationRepository().delete(fileRelation.id!)
    }

    this.findAndDeleteFileRelationFromFileBucket(fileRelation)

    onDeleted?.(fileRelation)
  }

  /**
   * Rotate a file.
   * @param file The file to rotate.
   * @param degrees The amount of degrees to rotate the file.
   */
  public async rotateFile(file: FileModel, degrees: number) {
    const response = await new FileRepository().rotate(file, degrees)

    const newFileModel = new FileModel(response.data)
    Object.assign(file, newFileModel)

    return file
  }

  public async modifyFile(fileModel: FileModel, file: File) {
    const response = await new FileRepository().modify(fileModel, file)

    const newFileModel = new FileModel(response.data)
    Object.assign(fileModel, newFileModel)

    return fileModel
  }

  private findAndDeleteFileRelationFromFileBucket(fileRelation: FileRelation) {
    for (const fileBucket of this.allFileBuckets.value) {
      const fileRelationIndex = fileBucket.fileRelations.findIndex(
        (relation: FileRelation) => relation.id === fileRelation.id
      )
      if (fileRelationIndex !== -1) {
        fileBucket.fileRelations.splice(fileRelationIndex, 1)
        return true
      }
    }
    return false
  }
}

/**
 * Collect all unique FileBuckets from the provided collections of models.
 * @param collections
 */
function collectAllFileBuckets(collections: Array<Array<BaseModel>>): FileBucket[] {
  const _allFileBuckets: FileBucket[] = []

  for (const collection of collections) {
    if (collection.length === 0) {
      continue
    }

    // Check first item of collection to see if it has any FileBucket properties
    const keys = discoverFileBucketKeys(collection[0])
    if (keys.length === 0) {
      continue
    }

    for (const item of collection) {
      for (const k of keys) {
        const bucket = (item as any)[k]
        if (bucket instanceof FileBucket) {
          _allFileBuckets.push(bucket)
        }
      }
    }
  }
  return _allFileBuckets
}

function discoverFileBucketKeys(sample: BaseModel): string[] {
  return Object.keys(sample).filter((k) => sample[k as keyof BaseModel] instanceof FileBucket)
}
